import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import {
  GamesGroupService,
  GroupType,
  PredictorGroupGames,
  PredictorGroupSummary,
  TriviaGroupGames,
  TriviaGroupSummary,
} from '@fgb/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Subscription, tap } from 'rxjs';

@Component({
  selector: 'fgbcl-games-list-modal',
  templateUrl: './games-list-modal.component.html',
  styleUrls: ['./games-list-modal.component.scss'],
})
export class GamesListModalComponent {
  groupTypes = GroupType;
  unsavedChanges = false;
  private navigationSubscription: Subscription;
  private isNavigating = false;
  gameDetails: TriviaGroupGames | PredictorGroupGames;
  @Input() gameGroup: TriviaGroupSummary | PredictorGroupSummary;
  @Input() groupType: GroupType;
  @Input() predictorStatus: string;
  @Output() closeModal = new EventEmitter();

  @ViewChild('content', { static: true }) content: ElementRef;

  constructor(
    private gamesGroupService: GamesGroupService,
    private modalService: NgbModal,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.fetchGroup();
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('popstate', this.handlePopState);
    this.navigationSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && this.unsavedChanges && !this.isNavigating) {
        this.isNavigating = true;
        this.openModel().then((confirmed) => {
          if (confirmed) {
            this.isNavigating = false;
            this.router.navigateByUrl(event.url);
          } else {
            this.isNavigating = false;
            history.pushState(null, '', this.router.url);
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('popstate', this.handlePopState);
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  // Handle the browser close, refresh, or navigation away using native browser dialog
  handleBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (this.unsavedChanges) {
      event.preventDefault();
    }
  };

  // Handle popstate event when the user navigates using the browser back/forward buttons
  handlePopState = (): void => {
    // Do not open the custom modal; let the beforeunload handle this
    // No need to alter state or show custom dialog; the native dialog will appear automatically
  };

  // Open the custom modal for confirming unsaved changes when navigating within the app
  openModel(): Promise<boolean> {
    const modalRef: NgbModalRef = this.modalService.open(this.content, {
      centered: true,
      windowClass: 'fgbcl-game-confirming-unsaved-model-custom-style .modal-dialog',
    });

    return new Promise<boolean>((resolve) => {
      modalRef.result.then(
        (result) => {
          if (result === 'confirm') {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        () => {
          resolve(false);
        }
      );
    });
  }

  // Handle manual modal close with custom buttons inside your Angular component
  closeModel(): void {
    if (this.unsavedChanges) {
      this.openModel().then((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.closeModal.emit();
      });
    } else {
      this.closeModal.emit();
    }
  }

  // Update the unsaved changes status when notified by the child component
  updateUnsavedChanges(status: boolean): void {
    this.unsavedChanges = status;
  }

  fetchGroup() {
    if (this.groupType == GroupType.Trivia) {
      firstValueFrom(
        this.gamesGroupService.fetchTriviaGroupGames(this.gameGroup.GroupId).pipe(
          tap((triviaGames) => {
            this.gameDetails = triviaGames;
          })
        )
      );
    }

    if (this.groupType == GroupType.Predictor) {
      firstValueFrom(
        this.gamesGroupService.fetchPredictorGroupGames(this.gameGroup.GroupId).pipe(
          tap((predictorGames) => {
            this.gameDetails = predictorGames;
          })
        )
      );
    }
  }
}
